/**
 * The help catalogue: one card per feature.
 *
 * The help page used to be a single rich-text blob an administrator typed into
 * a WYSIWYG editor. That put the burden of explaining the whole application on
 * whoever happened to open the editor, and in practice it stayed close to
 * empty -- so the page told a new officer nothing.
 *
 * These are the cards as shipped. An administrator can edit any of them under
 * Admin -> Help editor, in each offered language, and what they save is stored
 * with the other application settings and wins over what is here. Nothing is
 * lost by editing: the editor can restore a card, or all of them, to the text
 * below.
 *
 * ## Why the text lives here rather than in assets/i18n
 *
 * The i18n bundles hold interface chrome -- button labels, column headings --
 * which is translated once and rarely touched. This is CONTENT: it is expected
 * to be rewritten per deployment, because what "a control" or "a taxpayer"
 * means differs between revenue authorities. Keeping it out of the bundles is
 * what lets an administrator rewrite it without a release, and keeps a
 * half-translated bundle from hiding a card entirely.
 *
 * A card with no text in the reader's language falls back to English, which is
 * the same rule the rest of the application follows.
 */

/** The section a card belongs to. Rendered as a heading and matched by search. */
export type HelpCategory = 'start' | 'data' | 'analysis' | 'controls' | 'admin' | 'account';

export const HELP_CATEGORIES: HelpCategory[] = [
  'start', 'data', 'analysis', 'controls', 'admin', 'account',
];

/** Per-language text. `en` is required: everything falls back to it. */
export interface HelpText {
  en: string;
  fr?: string;
  nl?: string;
  [lang: string]: string | undefined;
}

export interface HelpCard {
  /** Stable across edits and releases -- an admin override is stored against it. */
  id: string;
  category: HelpCategory;
  /** A Lucide icon name, registered in app.module.ts. */
  icon: string;
  title: HelpText;
  body: HelpText;
}

export const DEFAULT_HELP_CARDS: HelpCard[] = [
  // ── Getting started ───────────────────────────────────────────────────────
  {
    id: 'signing-in',
    category: 'start',
    icon: 'lock',
    title: {
      en: 'Signing in',
      fr: 'Se connecter',
      nl: 'Aanmelden',
    },
    body: {
      en: 'Depending on how your authority is set up, you sign in either with your organisation account through single sign-on, or with an email address and password on this site. If your deployment uses both, the sign-in page offers the choice. You never receive a password from an administrator: you set your own from an invitation link.',
      fr: "Selon la configuration de votre administration, vous vous connectez soit avec votre compte d'organisation via l'authentification unique, soit avec une adresse e-mail et un mot de passe sur ce site. Si votre déploiement utilise les deux, la page de connexion propose le choix. Un administrateur ne vous transmet jamais de mot de passe : vous définissez le vôtre à partir d'un lien d'invitation.",
      nl: 'Afhankelijk van de inrichting bij uw dienst meldt u zich aan met uw organisatieaccount via eenmalige aanmelding, of met een e-mailadres en wachtwoord op deze site. Gebruikt uw omgeving beide, dan biedt de aanmeldpagina de keuze. U krijgt nooit een wachtwoord van een beheerder: u stelt het zelf in via een uitnodigingslink.',
    },
  },
  {
    id: 'mfa',
    category: 'start',
    icon: 'lock',
    title: {
      en: 'Two-step sign-in',
      fr: 'Connexion en deux étapes',
      nl: 'Aanmelden in twee stappen',
    },
    body: {
      en: 'Accounts on this site always require a second factor. The first time you sign in you choose one: an authenticator app on your phone, which shows a six-digit code that changes every thirty seconds, or a code sent to your email address. An authenticator app is the stronger of the two, because your email address is also your username.',
      fr: "Les comptes de ce site exigent toujours un second facteur. Lors de votre première connexion, vous en choisissez un : une application d'authentification sur votre téléphone, qui affiche un code à six chiffres renouvelé toutes les trente secondes, ou un code envoyé à votre adresse e-mail. L'application d'authentification est la plus sûre des deux, car votre adresse e-mail est aussi votre identifiant.",
      nl: 'Accounts op deze site vereisen altijd een tweede factor. Bij uw eerste aanmelding kiest u er een: een authenticator-app op uw telefoon, die een zescijferige code toont die elke dertig seconden verandert, of een code per e-mail. De authenticator-app is de sterkste van de twee, omdat uw e-mailadres tevens uw gebruikersnaam is.',
    },
  },
  {
    id: 'trusted-device',
    category: 'start',
    icon: 'lock',
    title: {
      en: 'Remembering your device',
      fr: 'Mémoriser votre appareil',
      nl: 'Uw apparaat onthouden',
    },
    body: {
      en: 'On the code screen you can tick "don\'t ask again on this device". That browser then skips the second factor for the number of days your administrator has configured. Only tick it on a device that is yours: the trust belongs to the browser, so any other computer still asks for a code, and an administrator can revoke it.',
      fr: "Sur l'écran du code, vous pouvez cocher « ne plus demander sur cet appareil ». Ce navigateur ignore alors le second facteur pendant le nombre de jours configuré par votre administrateur. Ne cochez cette case que sur un appareil qui vous appartient : la confiance est liée au navigateur, tout autre ordinateur demandera donc un code, et un administrateur peut la révoquer.",
      nl: 'Op het codescherm kunt u "niet meer vragen op dit apparaat" aanvinken. Die browser slaat de tweede factor dan over gedurende het aantal dagen dat uw beheerder heeft ingesteld. Vink dit alleen aan op een apparaat dat van u is: het vertrouwen hoort bij de browser, elke andere computer vraagt dus nog steeds om een code, en een beheerder kan het intrekken.',
    },
  },
  {
    id: 'forgot-password',
    category: 'start',
    icon: 'lock',
    title: {
      en: 'Forgotten password',
      fr: 'Mot de passe oublié',
      nl: 'Wachtwoord vergeten',
    },
    body: {
      en: 'Use the link on the sign-in page. A reset link is emailed to the address on your account and expires after a short time. Resetting a password does not remove your second factor: you still need your authenticator or an emailed code afterwards. If you have lost the second factor itself, ask an administrator to reset it for you.',
      fr: "Utilisez le lien sur la page de connexion. Un lien de réinitialisation est envoyé à l'adresse de votre compte et expire rapidement. Réinitialiser un mot de passe ne supprime pas votre second facteur : il vous faudra ensuite toujours votre application d'authentification ou un code par e-mail. Si vous avez perdu le second facteur lui-même, demandez à un administrateur de le réinitialiser.",
      nl: 'Gebruik de link op de aanmeldpagina. Er wordt een herstellink gemaild naar het adres van uw account; die verloopt na korte tijd. Een wachtwoord herstellen verwijdert uw tweede factor niet: u hebt daarna nog steeds uw authenticator of een gemailde code nodig. Bent u de tweede factor zelf kwijt, vraag dan een beheerder deze te resetten.',
    },
  },
  {
    id: 'languages',
    category: 'start',
    icon: 'info',
    title: {
      en: 'Changing language',
      fr: 'Changer de langue',
      nl: 'Taal wijzigen',
    },
    body: {
      en: 'The flags in the top bar switch the interface language, and your choice is remembered in this browser. Which languages appear there is set by an administrator under Settings. English is always available, because every other language falls back to it for anything not yet translated.',
      fr: "Les drapeaux de la barre supérieure changent la langue de l'interface, et votre choix est mémorisé dans ce navigateur. Les langues proposées sont définies par un administrateur dans les Paramètres. L'anglais est toujours disponible, car toutes les autres langues s'y replient pour ce qui n'est pas encore traduit.",
      nl: 'Met de vlaggen in de bovenbalk wisselt u de taal van de interface; uw keuze wordt in deze browser onthouden. Welke talen daar verschijnen, stelt een beheerder in bij Instellingen. Engels is altijd beschikbaar, omdat elke andere taal daarop terugvalt voor wat nog niet vertaald is.',
    },
  },

  // ── Your account ──────────────────────────────────────────────────────────
  {
    id: 'your-profile',
    category: 'account',
    icon: 'user',
    title: {
      en: 'Your profile',
      fr: 'Votre profil',
      nl: 'Uw profiel',
    },
    body: {
      en: 'Click your name at the top right to open your profile. You can set your first name and surname, which become the initials on your avatar, and choose the colour of that avatar. Your username, your roles and your account status are managed by an administrator and are not editable here.',
      fr: "Cliquez sur votre nom en haut à droite pour ouvrir votre profil. Vous pouvez y définir votre prénom et votre nom, qui forment les initiales de votre avatar, et choisir la couleur de cet avatar. Votre identifiant, vos rôles et le statut de votre compte sont gérés par un administrateur et ne sont pas modifiables ici.",
      nl: 'Klik rechtsboven op uw naam om uw profiel te openen. U kunt uw voornaam en achternaam instellen — die vormen de initialen op uw avatar — en de kleur van die avatar kiezen. Uw gebruikersnaam, uw rollen en uw accountstatus worden door een beheerder beheerd en zijn hier niet te wijzigen.',
    },
  },
  {
    id: 'report-issue',
    category: 'account',
    icon: 'bug',
    title: {
      en: 'Reporting a problem',
      fr: 'Signaler un problème',
      nl: 'Een probleem melden',
    },
    body: {
      en: '"Report an issue or bug", in the menu under your name, opens the issue list and lets you add one. Give it a short title and describe what you did, what you expected and what happened instead. Closed issues are hidden until you ask for them, so the list shows what is still outstanding.',
      fr: "« Signaler un problème ou un bogue », dans le menu sous votre nom, ouvre la liste des problèmes et permet d'en ajouter un. Donnez-lui un titre court et décrivez ce que vous avez fait, ce que vous attendiez et ce qui s'est produit à la place. Les problèmes clos sont masqués tant que vous ne les demandez pas, afin que la liste montre ce qui reste en cours.",
      nl: '"Een probleem of bug melden", in het menu onder uw naam, opent de lijst met meldingen en laat u er een toevoegen. Geef een korte titel en beschrijf wat u deed, wat u verwachtte en wat er in plaats daarvan gebeurde. Gesloten meldingen blijven verborgen tot u erom vraagt, zodat de lijst toont wat nog openstaat.',
    },
  },
  {
    id: 'release-notes',
    category: 'account',
    icon: 'notebook-text',
    title: {
      en: 'Release notes',
      fr: 'Notes de version',
      nl: 'Releasenotes',
    },
    body: {
      en: 'Release notes, in the menu under your name, summarise each version newest first, and show which version you are currently using. If a screen behaves differently from how you remember it, that page usually says why.',
      fr: "Les notes de version, dans le menu sous votre nom, résument chaque version de la plus récente à la plus ancienne et indiquent la version que vous utilisez. Si un écran se comporte autrement que dans votre souvenir, cette page en donne généralement la raison.",
      nl: 'De releasenotes, in het menu onder uw naam, vatten elke versie samen met de nieuwste bovenaan en tonen welke versie u gebruikt. Gedraagt een scherm zich anders dan u zich herinnert, dan staat daar meestal waarom.',
    },
  },

  // ── Data ──────────────────────────────────────────────────────────────────
  {
    id: 'datasets',
    category: 'data',
    icon: 'list',
    title: {
      en: 'Browsing a dataset',
      fr: 'Consulter un jeu de données',
      nl: 'Een dataset bekijken',
    },
    body: {
      en: 'A dataset is one imported file format: payments, declarations or whatever your authority loads. Pick one from the menu on the left, then filter by typing in the boxes under the column headings and sort by clicking a heading. What you see is limited to the taxpayers and datasets your role allows.',
      fr: "Un jeu de données correspond à un format de fichier importé : paiements, déclarations ou tout ce que votre administration charge. Choisissez-en un dans le menu de gauche, filtrez en saisissant du texte dans les cases sous les en-têtes de colonnes et triez en cliquant sur un en-tête. Ce que vous voyez est limité aux contribuables et aux jeux de données autorisés par votre rôle.",
      nl: 'Een dataset is één geïmporteerd bestandsformaat: betalingen, aangiften of wat uw dienst ook inleest. Kies er een in het menu links, filter door te typen in de vakjes onder de kolomkoppen en sorteer door op een kop te klikken. Wat u ziet is beperkt tot de belastingplichtigen en datasets die uw rol toestaat.',
    },
  },
  {
    id: 'export-data',
    category: 'data',
    icon: 'file-spreadsheet',
    title: {
      en: 'Exporting to Excel',
      fr: 'Exporter vers Excel',
      nl: 'Exporteren naar Excel',
    },
    body: {
      en: 'Any dataset view can be exported to Excel, and the export applies the filters currently on screen rather than dumping the whole table. A large export is prepared in the background, so leave the page open until the file arrives.',
      fr: "Toute vue d'un jeu de données peut être exportée vers Excel, et l'export applique les filtres affichés à l'écran plutôt que de vider la table entière. Un export volumineux est préparé en arrière-plan : laissez la page ouverte jusqu'à l'arrivée du fichier.",
      nl: 'Elke datasetweergave kan naar Excel worden geëxporteerd, en de export past de filters toe die op het scherm staan in plaats van de hele tabel te dumpen. Een grote export wordt op de achtergrond voorbereid; laat de pagina open tot het bestand er is.',
    },
  },
  {
    id: 'upload-data',
    category: 'data',
    icon: 'cloud-upload',
    title: {
      en: 'Importing a file',
      fr: 'Importer un fichier',
      nl: 'Een bestand importeren',
    },
    body: {
      en: 'Files are imported against a file format, which says what each column means: which one is the tax id, which is the amount, which is the date. Upload the file and the rows are processed in the background; the batch process screen shows progress and how many rows were read. Import the same file twice and the taxpayers are matched on their tax id rather than created again.',
      fr: "Les fichiers sont importés selon un format de fichier, qui indique la signification de chaque colonne : laquelle est l'identifiant fiscal, laquelle le montant, laquelle la date. Téléversez le fichier et les lignes sont traitées en arrière-plan ; l'écran des traitements par lot montre l'avancement et le nombre de lignes lues. Si vous importez deux fois le même fichier, les contribuables sont rapprochés par leur identifiant fiscal plutôt que recréés.",
      nl: 'Bestanden worden geïmporteerd volgens een bestandsformaat, dat aangeeft wat elke kolom betekent: welke het fiscaal nummer is, welke het bedrag, welke de datum. Upload het bestand en de regels worden op de achtergrond verwerkt; het scherm met batchverwerkingen toont de voortgang en hoeveel regels zijn gelezen. Importeert u hetzelfde bestand twee keer, dan worden belastingplichtigen gematcht op hun fiscaal nummer in plaats van opnieuw aangemaakt.',
    },
  },
  {
    id: 'taxpayers',
    category: 'data',
    icon: 'building-2',
    title: {
      en: 'Taxpayers',
      fr: 'Contribuables',
      nl: 'Belastingplichtigen',
    },
    body: {
      en: 'Every taxpayer is identified by its tax id, and there is exactly one record per tax id. Opening a taxpayer shows its details, the datasets it appears in and the documents attached to it. Corrections made here are kept: a later file import fills in fields that are blank but never overwrites what somebody has already corrected.',
      fr: "Chaque contribuable est identifié par son identifiant fiscal, et il existe exactement une fiche par identifiant. Ouvrir un contribuable affiche ses informations, les jeux de données où il apparaît et les documents qui lui sont rattachés. Les corrections faites ici sont conservées : un import ultérieur complète les champs vides mais n'écrase jamais ce qui a déjà été corrigé.",
      nl: 'Elke belastingplichtige wordt geïdentificeerd door zijn fiscaal nummer, en er is precies één record per nummer. Bij het openen ziet u de gegevens, de datasets waarin de belastingplichtige voorkomt en de bijgevoegde documenten. Correcties hier blijven behouden: een latere import vult lege velden aan, maar overschrijft nooit wat iemand al heeft gecorrigeerd.',
    },
  },
  {
    id: 'taxpayer-documents',
    category: 'data',
    icon: 'file-text',
    title: {
      en: 'Attaching documents',
      fr: 'Joindre des documents',
      nl: 'Documenten bijvoegen',
    },
    body: {
      en: 'Documents can be attached to a taxpayer and to a control: scanned declarations, correspondence, supporting evidence. They are stored with the record rather than on someone\'s desktop, so the next officer to open the file sees the same evidence you did.',
      fr: "Des documents peuvent être joints à un contribuable et à un contrôle : déclarations scannées, correspondance, pièces justificatives. Ils sont conservés avec la fiche plutôt que sur le poste de quelqu'un, de sorte que le prochain agent qui ouvre le dossier voit les mêmes pièces que vous.",
      nl: 'Documenten kunnen aan een belastingplichtige en aan een controle worden gekoppeld: gescande aangiften, correspondentie, bewijsstukken. Ze worden bij het dossier bewaard in plaats van op iemands bureaublad, zodat de volgende ambtenaar dezelfde stukken ziet als u.',
    },
  },

  // ── Analysis ──────────────────────────────────────────────────────────────
  {
    id: 'dashboards',
    category: 'analysis',
    icon: 'chart-pie',
    title: {
      en: 'Dashboards',
      fr: 'Tableaux de bord',
      nl: 'Dashboards',
    },
    body: {
      en: 'Dashboards summarise the imported data as charts and totals over a period you choose. Which charts exist, and what each one counts, is configured by an administrator, so ask them if a figure does not mean what you expect.',
      fr: "Les tableaux de bord résument les données importées sous forme de graphiques et de totaux sur une période que vous choisissez. Les graphiques disponibles et ce que chacun mesure sont configurés par un administrateur : adressez-vous à lui si un chiffre ne correspond pas à ce que vous attendez.",
      nl: 'Dashboards vatten de geïmporteerde gegevens samen als grafieken en totalen over een periode die u kiest. Welke grafieken er zijn en wat elke grafiek telt, wordt door een beheerder ingesteld; vraag het hen als een cijfer niet betekent wat u verwacht.',
    },
  },
  {
    id: 'indicators',
    category: 'analysis',
    icon: 'chart-line',
    title: {
      en: 'Indicators',
      fr: 'Indicateurs',
      nl: 'Indicatoren',
    },
    body: {
      en: 'An indicator is a rule applied to the data — a comparison, a threshold, a ratio — that produces a score per taxpayer. Indicators are grouped into sectors so that a rule meant for mining is not applied to retail.',
      fr: "Un indicateur est une règle appliquée aux données — une comparaison, un seuil, un ratio — qui produit une note par contribuable. Les indicateurs sont regroupés par secteur afin qu'une règle destinée aux mines ne s'applique pas au commerce de détail.",
      nl: 'Een indicator is een regel op de gegevens — een vergelijking, een drempel, een verhouding — die per belastingplichtige een score oplevert. Indicatoren zijn gegroepeerd per sector, zodat een regel voor mijnbouw niet op detailhandel wordt toegepast.',
    },
  },
  {
    id: 'risk-management',
    category: 'analysis',
    icon: 'trending-up',
    title: {
      en: 'Risk results',
      fr: 'Résultats de risque',
      nl: 'Risicoresultaten',
    },
    body: {
      en: 'Risk results rank taxpayers by their combined indicator scores for a year, so the ones worth looking at come first. Open a taxpayer\'s breakdown to see which indicators contributed and by how much — a ranking nobody can explain is not a reason to open a control.',
      fr: "Les résultats de risque classent les contribuables selon la combinaison de leurs notes d'indicateurs pour une année, afin que ceux qui méritent attention apparaissent en premier. Ouvrez le détail d'un contribuable pour voir quels indicateurs ont contribué et dans quelle mesure : un classement que personne ne peut expliquer ne justifie pas l'ouverture d'un contrôle.",
      nl: 'Risicoresultaten rangschikken belastingplichtigen op hun gecombineerde indicatorscores voor een jaar, zodat wie aandacht verdient bovenaan staat. Open de opsplitsing van een belastingplichtige om te zien welke indicatoren bijdroegen en hoeveel — een rangschikking die niemand kan uitleggen is geen reden om een controle te openen.',
    },
  },
  {
    id: 'reports',
    category: 'analysis',
    icon: 'file-spreadsheet',
    title: {
      en: 'Reports',
      fr: 'Rapports',
      nl: 'Rapporten',
    },
    body: {
      en: 'Reports are saved queries: choose the dataset, the columns, what to group by and what to total, and the result can be run again whenever it is needed and exported. Build the report once rather than rebuilding the same filter by hand every month.',
      fr: "Les rapports sont des requêtes enregistrées : choisissez le jeu de données, les colonnes, les regroupements et les totaux ; le résultat peut être relancé à tout moment et exporté. Construisez le rapport une fois plutôt que de refaire le même filtre à la main chaque mois.",
      nl: 'Rapporten zijn opgeslagen query\'s: kies de dataset, de kolommen, waarop gegroepeerd wordt en wat opgeteld moet worden; het resultaat kan telkens opnieuw worden uitgevoerd en geëxporteerd. Bouw het rapport één keer in plaats van elke maand hetzelfde filter met de hand te herhalen.',
    },
  },

  // ── Controls (CERBERE) ────────────────────────────────────────────────────
  {
    id: 'controls-overview',
    category: 'controls',
    icon: 'layout-grid',
    title: {
      en: 'The control module',
      fr: 'Le module de contrôle',
      nl: 'De controlemodule',
    },
    body: {
      en: 'CERBERE is where a control is carried out from start to finish: the file, its stages, who is assigned, what was found and what was decided. It is an optional module — if your authority does not use it, an administrator hides it and you will not see it in the menu.',
      fr: "CERBERE est l'endroit où un contrôle est mené du début à la fin : le dossier, ses étapes, les personnes affectées, les constats et les décisions. C'est un module optionnel : si votre administration ne l'utilise pas, un administrateur le masque et il n'apparaît pas dans le menu.",
      nl: 'CERBERE is waar een controle van begin tot eind wordt uitgevoerd: het dossier, de fasen, wie is toegewezen, wat is vastgesteld en wat is beslist. Het is een optionele module — gebruikt uw dienst deze niet, dan verbergt een beheerder hem en ziet u hem niet in het menu.',
    },
  },
  {
    id: 'control-process',
    category: 'controls',
    icon: 'list',
    title: {
      en: 'Working a control',
      fr: 'Traiter un contrôle',
      nl: 'Een controle behandelen',
    },
    body: {
      en: 'A control moves through fixed stages, and each stage has to be completed and approved before the next one opens. That order comes from the regulations, not from the software, which is why a stage cannot be skipped. The timeline shows where a file currently sits and who it is waiting on.',
      fr: "Un contrôle passe par des étapes fixes, et chaque étape doit être complétée puis approuvée avant l'ouverture de la suivante. Cet ordre découle de la réglementation, non du logiciel : c'est pourquoi une étape ne peut pas être sautée. La chronologie indique où se trouve un dossier et qui est attendu.",
      nl: 'Een controle doorloopt vaste fasen, en elke fase moet worden afgerond en goedgekeurd voordat de volgende opengaat. Die volgorde komt uit de regelgeving, niet uit de software; daarom kan een fase niet worden overgeslagen. De tijdlijn toont waar een dossier staat en op wie het wacht.',
    },
  },
  {
    id: 'control-teams',
    category: 'controls',
    icon: 'user',
    title: {
      en: 'Teams and assignment',
      fr: 'Équipes et affectation',
      nl: 'Teams en toewijzing',
    },
    body: {
      en: 'Controls are assigned to a team, and a team has members with roles that decide who may draft, who may submit and who may approve. You see the controls you are assigned to; a coordinator sees the team\'s.',
      fr: "Les contrôles sont affectés à une équipe, et une équipe a des membres dont les rôles déterminent qui peut rédiger, qui peut soumettre et qui peut approuver. Vous voyez les contrôles qui vous sont affectés ; un coordinateur voit ceux de l'équipe.",
      nl: 'Controles worden aan een team toegewezen, en een team heeft leden met rollen die bepalen wie mag opstellen, wie mag indienen en wie mag goedkeuren. U ziet de controles waaraan u bent toegewezen; een coördinator ziet die van het team.',
    },
  },
  {
    id: 'timesheets',
    category: 'controls',
    icon: 'list',
    title: {
      en: 'Timesheets',
      fr: 'Feuilles de temps',
      nl: 'Urenstaten',
    },
    body: {
      en: 'Time spent on a control is recorded against its stages and submitted for approval. It is what makes the cost of a control visible next to what the control recovered.',
      fr: "Le temps passé sur un contrôle est enregistré par étape puis soumis à approbation. C'est ce qui rend le coût d'un contrôle visible en regard de ce qu'il a permis de recouvrer.",
      nl: 'De tijd besteed aan een controle wordt per fase vastgelegd en ter goedkeuring ingediend. Daardoor wordt de kostprijs van een controle zichtbaar naast wat de controle heeft opgeleverd.',
    },
  },
  {
    id: 'proposals',
    category: 'controls',
    icon: 'file-text',
    title: {
      en: 'Proposals and decisions',
      fr: 'Propositions et décisions',
      nl: 'Voorstellen en beslissingen',
    },
    body: {
      en: 'A proposal is what an officer puts forward at the end of a stage, and a director approves or returns it with a reason. Both the proposal and the decision are kept with the file, so the record shows not only what was decided but who decided it and when.',
      fr: "Une proposition est ce qu'un agent soumet à la fin d'une étape ; un directeur l'approuve ou la renvoie avec un motif. La proposition et la décision sont toutes deux conservées avec le dossier : le registre montre donc non seulement ce qui a été décidé, mais aussi par qui et quand.",
      nl: 'Een voorstel is wat een ambtenaar aan het eind van een fase indient; een directeur keurt het goed of stuurt het met een motivering terug. Zowel het voorstel als de beslissing blijven bij het dossier, zodat het dossier niet alleen toont wat is beslist, maar ook door wie en wanneer.',
    },
  },
  {
    id: 'notifications',
    category: 'controls',
    icon: 'bell',
    title: {
      en: 'Notifications',
      fr: 'Notifications',
      nl: 'Meldingen',
    },
    body: {
      en: 'The bell in the top bar counts what is waiting for you: a control assigned to you, a proposal to approve, a stage returned for correction. Opening a notification takes you to the file it concerns.',
      fr: "La cloche dans la barre supérieure compte ce qui vous attend : un contrôle qui vous est affecté, une proposition à approuver, une étape renvoyée pour correction. Ouvrir une notification vous mène au dossier concerné.",
      nl: 'Het belletje in de bovenbalk telt wat op u wacht: een aan u toegewezen controle, een goed te keuren voorstel, een fase die ter correctie is teruggestuurd. Een melding openen brengt u naar het betreffende dossier.',
    },
  },

  // ── Administration ────────────────────────────────────────────────────────
  {
    id: 'settings',
    category: 'admin',
    icon: 'settings',
    title: {
      en: 'Settings',
      fr: 'Paramètres',
      nl: 'Instellingen',
    },
    body: {
      en: 'Admin → Settings holds the choices that apply to the whole deployment: the title on the landing page, which languages are offered, which optional modules appear in the menu, and the connection to your issue tracker. Changes take effect for everyone as soon as they are saved.',
      fr: "Admin → Paramètres regroupe les choix qui s'appliquent à tout le déploiement : le titre de la page d'accueil, les langues proposées, les modules optionnels affichés dans le menu et la connexion à votre gestionnaire de tickets. Les modifications prennent effet pour tout le monde dès l'enregistrement.",
      nl: 'Beheer → Instellingen bevat de keuzes die voor de hele omgeving gelden: de titel op de landingspagina, welke talen worden aangeboden, welke optionele modules in het menu verschijnen en de koppeling met uw issuetracker. Wijzigingen gelden voor iedereen zodra ze zijn opgeslagen.',
    },
  },
  {
    id: 'users-roles',
    category: 'admin',
    icon: 'user',
    title: {
      en: 'Users and roles',
      fr: 'Utilisateurs et rôles',
      nl: 'Gebruikers en rollen',
    },
    body: {
      en: 'A role decides what somebody can see and do, and it is checked by the server on every request — not just hidden in the menu. Give the narrowest role that lets the person do their work, and disable an account rather than deleting it when someone leaves, so their past work stays attributable.',
      fr: "Un rôle détermine ce qu'une personne peut voir et faire, et il est vérifié par le serveur à chaque requête, pas seulement masqué dans le menu. Attribuez le rôle le plus restreint permettant de travailler, et désactivez un compte plutôt que de le supprimer lors d'un départ, afin que le travail passé reste attribuable.",
      nl: 'Een rol bepaalt wat iemand mag zien en doen, en wordt bij elke aanvraag door de server gecontroleerd — niet alleen in het menu verborgen. Geef de smalste rol waarmee iemand zijn werk kan doen, en schakel een account uit in plaats van het te verwijderen als iemand vertrekt, zodat eerder werk toewijsbaar blijft.',
    },
  },
  {
    id: 'invitations',
    category: 'admin',
    icon: 'user',
    title: {
      en: 'Inviting a user',
      fr: 'Inviter un utilisateur',
      nl: 'Een gebruiker uitnodigen',
    },
    body: {
      en: 'New accounts are created by invitation: you enter the email address and the roles, and the person sets their own password and enrols a second factor from the emailed link. An administrator therefore never knows another user\'s password. An invitation expires, and can be sent again.',
      fr: "Les nouveaux comptes sont créés par invitation : vous saisissez l'adresse e-mail et les rôles, et la personne définit elle-même son mot de passe et enrôle un second facteur depuis le lien reçu. Un administrateur ne connaît donc jamais le mot de passe d'un autre utilisateur. Une invitation expire et peut être renvoyée.",
      nl: 'Nieuwe accounts ontstaan via een uitnodiging: u voert het e-mailadres en de rollen in, en de persoon stelt zelf een wachtwoord in en registreert een tweede factor via de gemailde link. Een beheerder kent dus nooit het wachtwoord van een andere gebruiker. Een uitnodiging verloopt en kan opnieuw worden verstuurd.',
    },
  },
  {
    id: 'reset-mfa',
    category: 'admin',
    icon: 'refresh-cw',
    title: {
      en: 'Resetting a second factor',
      fr: 'Réinitialiser un second facteur',
      nl: 'Een tweede factor resetten',
    },
    body: {
      en: 'When somebody loses their phone, an administrator clears their second factor from the user screen and they enrol again at their next sign-in. This does not weaken the account: the factor is still required, and the reset is recorded. Satisfy yourself that the request is genuine before doing it.',
      fr: "Lorsqu'une personne perd son téléphone, un administrateur efface son second facteur depuis l'écran utilisateur et elle le réenrôle à sa prochaine connexion. Cela n'affaiblit pas le compte : le facteur reste obligatoire et la réinitialisation est journalisée. Assurez-vous que la demande est authentique avant de la traiter.",
      nl: 'Verliest iemand zijn telefoon, dan wist een beheerder de tweede factor via het gebruikersscherm en registreert de persoon deze bij de volgende aanmelding opnieuw. Dit verzwakt het account niet: de factor blijft verplicht en de reset wordt vastgelegd. Vergewis u ervan dat het verzoek echt is voordat u dit doet.',
    },
  },
  {
    id: 'services-formats',
    category: 'admin',
    icon: 'cpu',
    title: {
      en: 'Services and file formats',
      fr: 'Services et formats de fichier',
      nl: 'Diensten en bestandsformaten',
    },
    body: {
      en: 'A service is a source of data, and each of its file formats describes one file layout: the separator, whether there is a header row, and what every column means. Getting the column types right is what makes the tax id, the amount and the date usable everywhere else in the application.',
      fr: "Un service est une source de données, et chacun de ses formats de fichier décrit une mise en page : le séparateur, la présence ou non d'une ligne d'en-tête, et la signification de chaque colonne. Le bon typage des colonnes est ce qui rend l'identifiant fiscal, le montant et la date exploitables partout ailleurs dans l'application.",
      nl: 'Een dienst is een gegevensbron, en elk bestandsformaat daarvan beschrijft één bestandsindeling: het scheidingsteken, of er een kopregel is, en wat elke kolom betekent. Kolommen correct typeren is wat het fiscaal nummer, het bedrag en de datum overal elders in de toepassing bruikbaar maakt.',
    },
  },
  {
    id: 'batch-processes',
    category: 'admin',
    icon: 'cpu',
    title: {
      en: 'Batch processes',
      fr: 'Traitements par lot',
      nl: 'Batchverwerkingen',
    },
    body: {
      en: 'Every import becomes a batch process, with its status and the number of rows it read. Check here when an upload appears to have done nothing: the file may still be processing, or it may have stopped on a row the format did not describe.',
      fr: "Chaque import donne lieu à un traitement par lot, avec son statut et le nombre de lignes lues. Consultez cet écran lorsqu'un téléversement semble sans effet : le fichier peut être encore en cours de traitement, ou s'être arrêté sur une ligne que le format ne décrivait pas.",
      nl: 'Elke import wordt een batchverwerking, met status en het aantal gelezen regels. Kijk hier wanneer een upload niets lijkt te hebben gedaan: het bestand kan nog verwerkt worden, of gestopt zijn op een regel die het formaat niet beschreef.',
    },
  },
  {
    id: 'currencies',
    category: 'admin',
    icon: 'trending-up',
    title: {
      en: 'Currencies and rates',
      fr: 'Devises et taux',
      nl: 'Valuta en koersen',
    },
    body: {
      en: 'Amounts arrive in whatever currency the source file uses, and are converted with the rate for the date of the transaction, not today\'s rate. Rates are fetched automatically; the rates screen is where you check a date that looks wrong or enter one by hand.',
      fr: "Les montants arrivent dans la devise du fichier source et sont convertis au taux de la date de l'opération, et non au taux du jour. Les taux sont récupérés automatiquement ; l'écran des taux permet de vérifier une date qui paraît erronée ou d'en saisir une manuellement.",
      nl: 'Bedragen komen binnen in de valuta van het bronbestand en worden omgerekend tegen de koers van de transactiedatum, niet de koers van vandaag. Koersen worden automatisch opgehaald; op het koersenscherm controleert u een datum die niet klopt of voert u er zelf een in.',
    },
  },
  {
    id: 'portal',
    category: 'admin',
    icon: 'layout-grid',
    title: {
      en: 'The public portal',
      fr: 'Le portail public',
      nl: 'Het publieke portaal',
    },
    body: {
      en: 'The portal publishes selected data to the public. Nothing is public by default: a dataset appears only once somebody marks it published and chooses which columns may be shown. Review that choice before publishing — it is the one part of the system where a mistake is visible outside the authority.',
      fr: "Le portail publie certaines données auprès du public. Rien n'est public par défaut : un jeu de données n'apparaît qu'une fois marqué comme publié et après le choix des colonnes visibles. Vérifiez ce choix avant de publier : c'est la seule partie du système où une erreur est visible en dehors de l'administration.",
      nl: 'Het portaal publiceert geselecteerde gegevens voor het publiek. Niets is standaard openbaar: een dataset verschijnt pas als iemand deze als gepubliceerd markeert en kiest welke kolommen zichtbaar mogen zijn. Controleer die keuze vóór publicatie — dit is het enige deel van het systeem waar een fout buiten de dienst zichtbaar is.',
    },
  },
  {
    id: 'newsletter',
    category: 'admin',
    icon: 'notebook-text',
    title: {
      en: 'Portal newsletter',
      fr: 'Lettre d\'information du portail',
      nl: 'Portaalnieuwsbrief',
    },
    body: {
      en: 'Visitors to the portal can subscribe to be told when new data is published. The subscriber list and the messages sent are managed here.',
      fr: "Les visiteurs du portail peuvent s'abonner pour être informés de la publication de nouvelles données. La liste des abonnés et les messages envoyés se gèrent ici.",
      nl: 'Bezoekers van het portaal kunnen zich abonneren om bericht te krijgen als er nieuwe gegevens worden gepubliceerd. De abonneelijst en de verzonden berichten beheert u hier.',
    },
  },
  {
    id: 'external-api',
    category: 'admin',
    icon: 'cpu',
    title: {
      en: 'External API connections',
      fr: 'Connexions API externes',
      nl: 'Externe API-koppelingen',
    },
    body: {
      en: 'Instead of uploading a file by hand, a dataset can be fetched from another system\'s API on a schedule. You configure the address, how to authenticate, and where in the response the rows are; from then on the data arrives on its own.',
      fr: "Plutôt que de téléverser un fichier à la main, un jeu de données peut être récupéré périodiquement depuis l'API d'un autre système. Vous configurez l'adresse, le mode d'authentification et l'emplacement des lignes dans la réponse ; ensuite, les données arrivent d'elles-mêmes.",
      nl: 'In plaats van een bestand met de hand te uploaden, kan een dataset volgens een schema uit de API van een ander systeem worden opgehaald. U stelt het adres in, hoe te authenticeren en waar in het antwoord de regels staan; daarna komen de gegevens vanzelf binnen.',
    },
  },
  {
    id: 'help-editor',
    category: 'admin',
    icon: 'circle-question-mark',
    title: {
      en: 'Editing this help',
      fr: 'Modifier cette aide',
      nl: 'Deze help bewerken',
    },
    body: {
      en: 'Admin → Help editor lists every card on this page and lets you rewrite its title and text in each offered language. Say what your authority actually does rather than what the software can do in general. Any card can be restored to the text it shipped with.',
      fr: "Admin → Éditeur d'aide liste chaque fiche de cette page et permet d'en réécrire le titre et le texte dans chaque langue proposée. Décrivez ce que fait réellement votre administration plutôt que ce que le logiciel permet en général. Chaque fiche peut être restaurée à son texte d'origine.",
      nl: 'Beheer → Help-editor toont elke kaart op deze pagina en laat u titel en tekst in elke aangeboden taal herschrijven. Beschrijf wat uw dienst daadwerkelijk doet in plaats van wat de software in het algemeen kan. Elke kaart kan worden hersteld naar de meegeleverde tekst.',
    },
  },
];

/** The text for a language, falling back to English -- never to nothing. */
export function textFor(text: HelpText | undefined, lang: string): string {
  if (!text) return '';
  const chosen = text[lang];
  if (typeof chosen === 'string' && chosen.trim() !== '') return chosen;
  return text.en || '';
}

/**
 * The catalogue as an administrator's saved edits leave it.
 *
 * Stored cards win over the shipped ones, matched on id, and a stored card that
 * no longer ships is dropped rather than shown: a card whose id has gone is one
 * this version of the application no longer has a feature for.
 *
 * The shipped ORDER is kept. Reordering is not something the editor offers, and
 * taking the order from stored data would let a save silently shuffle the page.
 */
export function mergeHelpCards(stored: any): HelpCard[] {
  const overrides = new Map<string, any>();
  if (Array.isArray(stored)) {
    for (const card of stored) {
      if (card && typeof card.id === 'string') overrides.set(card.id, card);
    }
  }

  return DEFAULT_HELP_CARDS.map(card => {
    const over = overrides.get(card.id);
    if (!over) return card;
    return {
      ...card,
      title: mergeText(card.title, over.title),
      body: mergeText(card.body, over.body),
    };
  });
}

/** A blank override means "use what shipped", not "show an empty card". */
function mergeText(base: HelpText, over: any): HelpText {
  if (!over || typeof over !== 'object') return base;
  const out: HelpText = { ...base };
  for (const lang of Object.keys(over)) {
    const value = over[lang];
    if (typeof value === 'string' && value.trim() !== '') out[lang] = value;
  }
  return out;
}

/**
 * The cards matching a search, in catalogue order.
 *
 * Matches the title and body IN EVERY LANGUAGE, not only the one on screen: an
 * officer who knows a feature by its French name should find it while reading
 * the English page. Accents are folded, so "controle" finds "contrôle".
 */
export function searchHelpCards(cards: HelpCard[], term: string): HelpCard[] {
  const needle = fold(term);
  if (!needle) return cards;
  const words = needle.split(/\s+/).filter(w => w.length > 0);

  return cards.filter(card => {
    const haystack = fold([
      card.id,
      card.category,
      ...Object.values(card.title || {}),
      ...Object.values(card.body || {}),
    ].filter(Boolean).join(' '));
    // Every word has to appear somewhere: two words narrow the list rather
    // than widening it, which is what a person typing a phrase expects.
    return words.every(word => haystack.indexOf(word) !== -1);
  });
}

function fold(value: string | undefined): string {
  return (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}
